import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * `Badge` — small count or label badge, typically decorating a button,
 * nav item, or card corner.
 *
 * Differs from `StatusPill` (semantic intent, dot+label) and `Chip`
 * (chip/secondary display pattern) by purpose: a Badge is a compact
 * quantity/state annotation — think unread-count, "New", version strings.
 *
 * Variants:
 *   - `default`  — the Chip/secondary surface (matches the workspace
 *                  secondary button language); neutral call-out.
 *   - `accent`   — brand-accent fill (high-priority callout, e.g. "New").
 *   - `success`  — semantic success via #2405 contract token.
 *   - `warning`  — semantic warning via #2405 contract token.
 *   - `error`    — semantic error via #2405 contract token (e.g. unread errors).
 *   - `info`     — semantic info via #2405 contract token.
 *
 * Token names (`--color-success`, `--color-warning`, `--color-error`,
 * `--color-info`) are defined in globals.css by PR #2405.
 * Fallback hues keep the badge legible during development.
 *
 * Sizes:
 *   - `default` — standard label badge (px-2, py-0.5, text-[10px]).
 *   - `dot`     — 6 px round dot (no children; presence alone conveys state).
 *
 * Non-interactive — no `app-region-no-drag` or focus styles. If a badge
 * is placed inside a draggable window title bar area, add
 * `app-region-no-drag` to its wrapper.
 */
const badgeVariants = cva(
  'inline-flex shrink-0 items-center justify-center rounded-full font-medium leading-none',
  {
    variants: {
      variant: {
        default: 'border border-white/[0.12] bg-white/[0.06] text-white/60',
        accent: 'bg-crucible-accent text-white',
        success:
          'bg-[color-mix(in_srgb,var(--color-success,#4ade80)_20%,transparent)] text-[var(--color-success,#4ade80)]',
        warning:
          'bg-[color-mix(in_srgb,var(--color-warning,#facc15)_20%,transparent)] text-[var(--color-warning,#facc15)]',
        error:
          'bg-[color-mix(in_srgb,var(--color-error,#f87171)_20%,transparent)] text-[var(--color-error,#f87171)]',
        info: 'bg-[color-mix(in_srgb,var(--color-info,#60a5fa)_20%,transparent)] text-[var(--color-info,#60a5fa)]',
      },
      size: {
        default: 'px-2 py-0.5 text-[10px]',
        dot: 'h-1.5 w-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({
  variant,
  size,
  className,
  children,
  ...props
}: BadgeProps): React.JSX.Element {
  return (
    <span className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {children}
    </span>
  );
}

export { badgeVariants };
